/**
 * Cuando la plata cambia, se refresca TODO lo que la muestra (auditoría de Smarty Drop, 26-sep).
 *
 * 🚨 EL HALLAZGO. Cada botón que mueve plata invalidaba a mano su propio subconjunto de claves —el
 *    saldo del encabezado, los movimientos, sus pedidos—, y las claves que llegaron después (la
 *    liquidación, los saldos por cuenta, lo que el almacén tiene por cobrar) no las invalidaba nadie.
 *    Se pagaba, se rendía o se confirmaba un cobro y la Liquidación seguía diciendo lo de antes hasta
 *    recargar la página. Es el mismo defecto que ya había mordido dos veces (ver `clavesDeCache.ts`).
 *
 * ⭐ LO ANUNCIA QUIEN MUEVE LA PLATA, LO REFRESCA UNO SOLO. Cada función de la biblioteca que llama a
 *    la base para mover plata, apenas la base acepta, llama a `laPlataCambio()`. Un único oyente
 *    montado en la raíz del panel (`<LaPlataSeRefresca />`) refresca la familia entera. Ninguna
 *    pantalla tiene que acordarse: la pantalla nueva que pague algo ya refresca, porque llama a la
 *    misma función. La prueba `laPlataSeRefrescaEntera` llama a cada una y verifica que anuncie.
 *
 * 🪤 LAS CLAVES VIVEN ACÁ. Una pantalla nueva que MUESTRE plata se suma a `CLAVES_DE_LA_PLATA`; los
 *    avisos en vivo que hablan de plata refrescan la misma lista (`useNotificacionesEnVivo`).
 */
import { useEffect } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { CLAVES_DROP, type ClaveDrop } from './clavesDeCache';

export const CLAVES_DE_LA_PLATA: readonly ClaveDrop[] = [
  CLAVES_DROP.miSaldo,
  CLAVES_DROP.movimientos,
  CLAVES_DROP.misRecargas,
  CLAVES_DROP.misPedidos,
  CLAVES_DROP.previoDeVenta,
  CLAVES_DROP.cumplimiento,
  CLAVES_DROP.cobros,
  CLAVES_DROP.pagosPorRevisar,
  CLAVES_DROP.billeteraDeLaPlataforma,
  CLAVES_DROP.porCobrar,
  CLAVES_DROP.liquidacion,
  CLAVES_DROP.repartoDeLaVenta,
  CLAVES_DROP.misSaldos,
  // El estado de cuenta (20261028009000): un periodo en curso cambia con cada asiento.
  CLAVES_DROP.estadoDeCuenta,
  CLAVES_DROP.miPorRecibir,
  CLAVES_DROP.proximoPago,
  CLAVES_DROP.misPagos,
  CLAVES_DROP.pagosPorTransferir,
  CLAVES_DROP.miRendicion,
  CLAVES_DROP.rendicionesDeclaradas,
  CLAVES_DROP.historialDeRendiciones,
  CLAVES_DROP.cobrosPorConfirmar,
  CLAVES_DROP.ajustesRecientes,
  CLAVES_DROP.porRendir,
  CLAVES_DROP.pagosDeLosCouriers,
  CLAVES_DROP.despachosPorPrepagar,
  CLAVES_DROP.despachoDeLaVentaPropia,
];

/** Invalida todo lo que muestra plata. */
export async function refrescarLaPlata(queryClient: QueryClient): Promise<void> {
  await Promise.all(CLAVES_DE_LA_PLATA.map((k) => queryClient.invalidateQueries({ queryKey: [k] })));
}

type Oyente = () => void;
const oyentes = new Set<Oyente>();

/** Lo llama cada función que mueve plata, DESPUÉS de que la base aceptó (nunca si falló). */
export function laPlataCambio(): void {
  for (const oyente of oyentes) oyente();
}

/** Suscribe a quien refresca. Devuelve cómo desuscribirse. */
export function alCambiarLaPlata(oyente: Oyente): () => void {
  oyentes.add(oyente);
  return () => { oyentes.delete(oyente); };
}

/** El oyente de la raíz del panel: cada anuncio refresca la familia entera. */
export function LaPlataSeRefresca(): null {
  const queryClient = useQueryClient();
  useEffect(() => alCambiarLaPlata(() => { void refrescarLaPlata(queryClient); }), [queryClient]);
  return null;
}
